import PropTypes from 'prop-types';
import { useState, useEffect, useMemo } from 'react';
import { useI18n } from '../../../../context/I18nContext';
import { useMatrixClient } from '../../../../context/MatrixContext';
import { useAvatar } from '../../../../context/AvatarContext';
import { useDisambiguation } from '../../../../context/DisambiguationContext';
import logger from '../../../../utils/logger';
import './ContactRemovalList.css';

const ContactRemovalItem = ({
  name = '',
  isCurrentUser = false,
  avatar = '',
  isRemovable = true,
  onRemove = () => {},
  matrixId = null,
}) => {
  const { t } = useI18n();

  return(
    <div className="contact-removal-item">
      <div className="contact-removal-avatar-wrapper">
        <img className="contact-removal-avatar" src={avatar} alt={`${name}'s avatar`} crossOrigin="anonymous" />
        {
          isRemovable && (
            <button className="contact-removal-close" onClick={onRemove}>
              <span className="material-icons">close</span>
            </button>
          )
        }
      </div>
      <span className="contact-removal-name" title={matrixId ? `${name} (${matrixId})` : name}>
        {(isCurrentUser) ? t('you') : name}
      </span>
      {matrixId && (
        <span className="contact-removal-mxid" title={matrixId}>{matrixId}</span>
      )}
    </div>
  );
};

ContactRemovalItem.propTypes = {
  name: PropTypes.string,
  isCurrentUser: PropTypes.bool,
  avatar: PropTypes.string,
  isRemovable: PropTypes.bool,
  onRemove: PropTypes.func,
  matrixId: PropTypes.string,
};

const ContactRemovalList = ({
  contacts = [],
  onContactRemove = () => {},
  isRemovable = (_contactId) => true,
}) => {
  const client = useMatrixClient();
  const { getUserAvatar } = useAvatar();
  const { needsDisambiguation } = useDisambiguation();
  const myUserId = client?.getUserId();
  const currentUser = (myUserId) ? client?.getUser(myUserId) : null;
  const [currentUserAvatar, setCurrentUserAvatar] = useState(null);

  // Fetch the current user's avatar via AvatarContext
  useEffect(() => {
    if (!client || !currentUser) return;

    let isMounted = true;

    getUserAvatar(currentUser.userId, currentUser.displayName, 96)
      .then(avatarUrl => {
        if (isMounted) {
          setCurrentUserAvatar(avatarUrl);
        }
      })
      .catch(err => {
        logger.error('Failed to acquire current user avatar:', err);
      });

    return () => {
      isMounted = false;
      // No cleanup needed - AvatarContext handles it automatically
    };
  }, [client, currentUser, getUserAvatar]);

  const displayedContacts = useMemo(() => {
    if (!currentUser) return contacts;

    const currentUserWithAvatar = {
      ...currentUser,
      avatar: currentUserAvatar, // Use the fetched avatar
    };

    return [...contacts, currentUserWithAvatar].filter(Boolean);
  }, [contacts, currentUser, currentUserAvatar]);

  return (
    <div className="contact-removal-list">
      {
        displayedContacts.map(contact => {
          const isCurrentUser = currentUser && contact.id === currentUser.id;

          const shouldShowMxId = !isCurrentUser && needsDisambiguation(contact.id);

          return (
            <ContactRemovalItem
              key={(isCurrentUser) ? 'currentUser' : contact.id}
              name={contact.name}
              isCurrentUser={isCurrentUser}
              avatar={contact.avatar}
              isRemovable={!isCurrentUser && isRemovable(contact.id)}
              onRemove={() => onContactRemove(contact.id)}
              matrixId={shouldShowMxId ? contact.id : null}
            />
          );
        })
      }
    </div>
  );
};

ContactRemovalList.propTypes = {
  contacts: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    avatar: PropTypes.string.isRequired,
  })),
  onContactRemove: PropTypes.func,
  isRemovable: PropTypes.func,
};

export default ContactRemovalList;
